import ScrollReveal from "@/components/base/ScrollReveal";

export default function TermsOfService() {
  return (
    <main>
      <section className="relative bg-bg-section pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/2 -right-32 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                Legal
              </span>
              <h1 className="font-serif text-[36px] md:text-[52px] lg:text-[60px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                Terms of Service
              </h1>
              <p className="font-sans text-[16px] text-text-muted">
                Last updated: May 18, 2026
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="relative bg-bg py-16 md:py-24 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <div className="flex flex-col gap-12 font-sans text-[16px] text-text-body leading-[1.8]">

                {/* Plain-language summary box */}
                <div className="p-6 bg-bg-card border border-border rounded-xl">
                  <p className="font-semibold text-text-headline mb-2">The short version</p>
                  <p className="text-text-muted">
                    By using this site you agree to use it respectfully and lawfully. The content here is ours and cannot be used or reproduced without written permission.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    1. Agreement to Terms
                  </h2>
                  <p className="text-text-muted">
                    By accessing or using the VantageSight AI Advisory LLC website (&ldquo;Site&rdquo;) at vantagesight.readdy.co, you agree to be bound by these Terms of Service. If you do not agree, please do not use the Site. We may update these Terms from time to time; continued use of the Site after an update means you accept the revised Terms.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    2. What VantageSight Does
                  </h2>
                  <p className="text-text-muted">
                    VantageSight AI Advisory LLC provides independent advisory services in AI strategy, governance, workforce readiness, and adoption, assessment design and results analysis, and workshop design and facilitation. We do not build AI systems, develop software, provide legal or financial advice, or endorse specific AI vendors or tools. Our role is purely advisory — helping organizations think clearly and act responsibly regarding AI.
                  </p>
                  <p className="text-text-muted mt-3">
                    We also plan to offer educational courses on AI topics in the future. When available, those courses will be governed by separate purchase agreements provided at the time of enrollment.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    3. This Site Is Informational Only
                  </h2>
                  <p className="text-text-muted">
                    The content on this Site — including articles, service descriptions, frameworks, and any other materials — is for general informational purposes only. It does not constitute professional, legal, financial, or technical advice. Decisions made based on information from this Site are your responsibility. For advice specific to your organization&rsquo;s situation, please engage us directly through a formal advisory arrangement.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    4. Advisory Engagements
                  </h2>
                  <p className="text-text-muted">
                    Scheduling a call through this site does not create a client relationship or any contractual obligation between you and VantageSight AI Advisory LLC. A formal advisory engagement only begins when both parties have signed a written services agreement. Any work we perform for you will be governed by the terms of that separate agreement, not by these Terms of Service.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    5. Intellectual Property
                  </h2>
                  <p className="text-text-muted">
                    All content on this Site — including text, frameworks, graphics, and branding — is the property of VantageSight AI Advisory LLC and is protected by applicable copyright and intellectual property laws. You may not reproduce, distribute, or use our content for commercial purposes without our prior written permission. Linking to this site is welcome.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    6. Acceptable Use
                  </h2>
                  <p className="text-text-muted mb-3">
                    You agree to use this Site only for lawful purposes. The following are not permitted:
                  </p>
                  <ul className="flex flex-col gap-2 list-disc pl-6">
                    <li className="text-text-muted">Attempting to disrupt, hack, or interfere with the Site&apos;s normal operation</li>
                    <li className="text-text-muted">Scraping or bulk-harvesting content from the Site without authorization</li>
                    <li className="text-text-muted">Using the contact form or Calendly integration to send spam or unsolicited commercial messages</li>
                    <li className="text-text-muted">Impersonating another person or misrepresenting your affiliation</li>
                  </ul>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    7. Third-Party Services
                  </h2>
                  <p className="text-text-muted">
                    This Site uses Calendly for scheduling and is hosted on the Readdy platform. These are independent services with their own terms and privacy policies. We are not responsible for the practices or content of third-party services linked from or embedded in this Site.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    8. Disclaimer of Warranties
                  </h2>
                  <p className="text-text-muted">
                    This Site and its content are provided &ldquo;as is&rdquo; without warranties of any kind, express or implied. We make no guarantee that the Site will be available at all times, error-free, or that the information on it is complete or up to date. Use of the Site is at your own risk.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    9. Limitation of Liability
                  </h2>
                  <p className="text-text-muted">
                    To the maximum extent permitted by law, VantageSight AI Advisory LLC shall not be liable for any indirect, incidental, or consequential damages arising from your use of this Site or reliance on its content. Our total liability for any claim arising from use of this Site is limited to zero dollars, as no fee is charged for accessing it.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    10. Governing Law
                  </h2>
                  <p className="text-text-muted">
                    These Terms are governed by the laws of the United States. Any disputes arising from use of this Site that cannot be resolved informally shall be subject to binding arbitration or the jurisdiction of courts in the state where VantageSight AI Advisory LLC is registered.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    11. Contact
                  </h2>
                  <p className="text-text-muted">
                    Questions about these Terms? Reach out:
                  </p>
                  <div className="mt-4 p-6 bg-bg-card border border-border rounded-xl">
                    <p className="text-text-headline font-semibold mb-2">VantageSight AI Advisory LLC</p>
                    <p className="text-text-muted">
                      Email:{" "}
                      <a href="mailto:smurray@vantagesightai.com" className="text-primary hover:underline">
                        smurray@vantagesightai.com
                      </a>
                    </p>
                  </div>
                </div>

              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </main>
  );
}